/**
 * TelegramBotCard — self-serve Telegram-bot provisioning surface.
 *
 * Three render states:
 *   1. Plan-locked (no `telegram_bot` feature) → upsell the Pro plan.
 *   2. Not set up                              → BotFather instructions + token field.
 *   3. Active (or disabled)                    → bot link, copy/disable controls.
 *
 * The card lives inside `pages/settings/IntegrationsSettings` and is
 * intentionally self-contained — pulling its own status, calling its
 * own endpoints, owning its own toast/error UX. That isolation lets us
 * iterate on the Telegram flow without disturbing the broader OAuth
 * integrations card grid.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bot,
  Check,
  Copy,
  ExternalLink,
  Loader2,
  Lock,
  Sparkles,
} from "lucide-react";

import { getCurrentOrg } from "@/api/organizations";
import {
  disconnectTelegramBot,
  getTelegramStatus,
  setupTelegramBot,
  type TelegramBotStatus,
} from "@/api/integrationsTelegram";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { usePlanFeatures } from "@/hooks/usePlanFeatures";
import { useToast } from "@/hooks/useToast";

function suggestUsername(slug: string | null | undefined): string {
  // Mirror the backend's `suggest_username` so the BotFather prompt
  // always matches what the customer will be told to use. Telegram
  // usernames must end in "bot" — the suffix here covers it.
  const base = (slug || "company")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${base.slice(0, 25)}_hr_bot`;
}

export function TelegramBotCard() {
  const { features, has, loading: featuresLoading } = usePlanFeatures();
  const planLocked = features != null && !has("telegram_bot");
  const { addToast } = useToast();

  const [status, setStatus] = useState<TelegramBotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgName, setOrgName] = useState<string>("");
  const [orgSlug, setOrgSlug] = useState<string>("");
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const refresh = useCallback(async () => {
    if (planLocked) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await getTelegramStatus();
      setStatus(s);
    } catch {
      // 402 / 403 land here when plan-gate trips between client and
      // server — treat as "not configured" rather than crashing.
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [planLocked]);

  useEffect(() => {
    if (featuresLoading) return;
    void refresh();
  }, [refresh, featuresLoading]);

  // Org name + slug fuel the BotFather pre-fill copy. Lazy-fetched on
  // first render — the org page already calls this elsewhere, so most
  // sessions hit a warm cache.
  useEffect(() => {
    let cancelled = false;
    void getCurrentOrg()
      .then((o) => {
        if (cancelled) return;
        setOrgName(o.name);
        setOrgSlug(o.slug);
      })
      .catch(() => {
        /* leave defaults */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const suggestedName = useMemo(
    () => `${orgName || "Your Company"} HR`,
    [orgName]
  );
  const suggestedUsername = useMemo(
    () => suggestUsername(orgSlug),
    [orgSlug]
  );

  const handleActivate = async () => {
    const trimmed = token.trim();
    if (!trimmed.includes(":")) {
      addToast(
        "error",
        "That doesn't look like a bot token. Copy the full string from @BotFather."
      );
      return;
    }
    setSubmitting(true);
    try {
      const s = await setupTelegramBot(trimmed);
      setStatus(s);
      setToken("");
      setShowInstructions(false);
      addToast("success", `@${s.bot_username} is live!`);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not activate the bot. Try again.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      const s = await disconnectTelegramBot();
      setStatus(s);
      addToast("success", "Telegram agent disabled.");
    } catch {
      addToast("error", "Could not disable the bot. Try again.");
    } finally {
      setDisconnecting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!status?.bot_link) return;
    try {
      await navigator.clipboard.writeText(status.bot_link);
      addToast("success", "Link copied.");
    } catch {
      addToast("error", "Couldn't copy — copy manually.");
    }
  };

  // ── Render ──────────────────────────────────────────────────────

  return (
    <Card className={planLocked ? "opacity-80" : undefined}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-accent/10 p-2">
            <Bot className="h-5 w-5 text-accent" aria-hidden="true" />
          </div>
          <div>
            <h3 className="text-sm font-semibold font-heading text-text-primary">
              Telegram Agent
            </h3>
            <p className="text-[11px] uppercase tracking-wider text-text-muted">
              Communication
            </p>
          </div>
        </div>
        {!planLocked && status?.active && (
          <Badge variant="success">Active</Badge>
        )}
        {!planLocked && status && !status.active && (
          <Badge variant="default">Disabled</Badge>
        )}
        {planLocked && (
          <Badge variant="default" className="uppercase tracking-wide">
            Pro
          </Badge>
        )}
      </div>

      {/* ── State 1: plan-locked ──────────────────────────────────── */}
      {planLocked && (
        <>
          <p className="text-sm text-text-muted mb-4">
            Give your team a dedicated Telegram agent for PTO checks, leave
            requests, manager approvals, and directory search — right in
            their chat client.
          </p>
          <div className="rounded-lg border border-dashed border-border bg-background p-4 text-sm flex items-start gap-3">
            <Lock className="h-4 w-4 mt-0.5 text-text-muted shrink-0" aria-hidden="true" />
            <div>
              <p className="text-text-primary font-medium">
                Upgrade to Professional
              </p>
              <p className="text-text-muted">
                The Telegram agent is included on Professional and Enterprise
                plans (and during your trial).
              </p>
            </div>
          </div>
          <div className="mt-4">
            <a
              href="/settings/billing"
              className="inline-flex items-center gap-2 rounded-lg border border-accent bg-accent px-3 py-2 text-sm font-medium text-white hover:bg-accent/90"
            >
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              View plans
            </a>
          </div>
        </>
      )}

      {/* ── States 2+3: real card ──────────────────────────────────── */}
      {!planLocked && loading && (
        <div className="flex items-center gap-2 text-sm text-text-muted py-4">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading…
        </div>
      )}

      {!planLocked && !loading && status?.active && (
        <ActiveState
          status={status}
          onCopyLink={handleCopyLink}
          onDisconnect={handleDisconnect}
          disconnecting={disconnecting}
        />
      )}

      {!planLocked && !loading && status && !status.active && (
        <DisabledState
          onReenable={() => setShowInstructions(true)}
          showInstructions={showInstructions}
          orgName={suggestedName}
          orgUsername={suggestedUsername}
          token={token}
          setToken={setToken}
          onActivate={handleActivate}
          submitting={submitting}
        />
      )}

      {!planLocked && !loading && !status && (
        <NotSetUpState
          orgName={suggestedName}
          orgUsername={suggestedUsername}
          token={token}
          setToken={setToken}
          onActivate={handleActivate}
          submitting={submitting}
        />
      )}
    </Card>
  );
}


// ─── State sub-components ────────────────────────────────────────────


function NotSetUpState(props: {
  orgName: string;
  orgUsername: string;
  token: string;
  setToken: (v: string) => void;
  onActivate: () => void;
  submitting: boolean;
}) {
  return (
    <>
      <p className="text-sm text-text-muted mb-4">
        Stand up a dedicated bot so your team can check PTO, request time
        off, search the directory, and approve requests right inside
        Telegram.
      </p>
      <BotFatherInstructions
        orgName={props.orgName}
        orgUsername={props.orgUsername}
      />
      <TokenForm
        token={props.token}
        setToken={props.setToken}
        onActivate={props.onActivate}
        submitting={props.submitting}
      />
    </>
  );
}


function DisabledState(props: {
  onReenable: () => void;
  showInstructions: boolean;
  orgName: string;
  orgUsername: string;
  token: string;
  setToken: (v: string) => void;
  onActivate: () => void;
  submitting: boolean;
}) {
  return (
    <>
      <p className="text-sm text-text-muted mb-4">
        Telegram agent is disabled. Re-enable to start receiving messages
        from your team again.
      </p>
      {!props.showInstructions ? (
        <Button size="sm" onClick={props.onReenable}>
          Re-enable
        </Button>
      ) : (
        <>
          <BotFatherInstructions
            orgName={props.orgName}
            orgUsername={props.orgUsername}
          />
          <TokenForm
            token={props.token}
            setToken={props.setToken}
            onActivate={props.onActivate}
            submitting={props.submitting}
          />
        </>
      )}
    </>
  );
}


function ActiveState(props: {
  status: TelegramBotStatus;
  onCopyLink: () => void;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  return (
    <>
      <p className="text-sm mb-2 text-text-primary">
        <Check
          className="inline h-4 w-4 -mt-0.5 mr-1 text-success"
          aria-hidden="true"
        />
        <strong>@{props.status.bot_username}</strong> is active
      </p>

      <div className="rounded-lg border border-border bg-background p-3 mt-2 mb-4 space-y-2">
        <p className="text-xs text-text-muted font-medium">
          Share this link with your team
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 truncate rounded bg-surface px-2 py-1 text-xs text-text-primary">
            {props.status.bot_link}
          </code>
          <Button
            size="sm"
            variant="ghost"
            onClick={props.onCopyLink}
            aria-label="Copy bot link"
          >
            <Copy className="h-3 w-3" aria-hidden="true" />
          </Button>
          {props.status.bot_link && (
            <a
              href={props.status.bot_link}
              target="_blank"
              rel="noreferrer"
              aria-label="Open bot in Telegram"
              className="inline-flex items-center text-xs text-accent hover:underline"
            >
              <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-dashed border-border bg-background p-3 text-xs text-text-muted space-y-1.5 mb-4">
        <p className="text-text-primary font-medium">Tell your team</p>
        <p>
          Each employee opens the bot, taps <strong>Start</strong>, and types
          their work email when prompted. The bot links their Telegram
          account to their Lenavio profile — that's it.
        </p>
        <p>
          From then on they can check PTO with <code>/pto</code>, file leave
          with <code>/request</code>, search the directory with{" "}
          <code>/directory</code>, and managers can approve requests with
          inline buttons.
        </p>
      </div>

      <Button
        size="sm"
        variant="ghost"
        onClick={props.onDisconnect}
        isLoading={props.disconnecting}
      >
        Disable bot
      </Button>
    </>
  );
}


// ─── Reused snippets ────────────────────────────────────────────────


function BotFatherInstructions(props: { orgName: string; orgUsername: string }) {
  return (
    <div className="rounded-lg border border-border bg-background p-4 mb-4 text-sm space-y-2">
      <p className="text-text-primary font-medium">
        Create your Telegram HR agent in 3 steps:
      </p>
      <ol className="list-decimal list-inside text-text-muted space-y-1.5">
        <li>
          Open Telegram and message{" "}
          <a
            href="https://t.me/BotFather"
            target="_blank"
            rel="noreferrer"
            className="text-accent hover:underline"
          >
            @BotFather
          </a>
        </li>
        <li>
          Send <code>/newbot</code> and follow the prompts
          <ul className="ml-5 mt-1 list-disc text-xs">
            <li>
              Name: <code>{props.orgName}</code>
            </li>
            <li>
              Username: <code>{props.orgUsername}</code>
            </li>
          </ul>
        </li>
        <li>BotFather will give you a token. Paste it below.</li>
      </ol>
    </div>
  );
}


function TokenForm(props: {
  token: string;
  setToken: (v: string) => void;
  onActivate: () => void;
  submitting: boolean;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium uppercase tracking-wide text-text-muted">
        Bot token
      </label>
      {/*
        type="password" so the token is masked in the field, never
        appears in browser autofill / save-password prompts under a
        plaintext alias, and screenshots / screen-shares don't leak
        it. ``autoComplete="off"`` further discourages the browser
        from caching it. The token never round-trips back from the
        backend either — the /setup response only returns username + link.
      */}
      <Input
        type="password"
        value={props.token}
        onChange={(e) => props.setToken(e.target.value)}
        placeholder="1234567890:AAH..."
        autoComplete="off"
        spellCheck={false}
      />
      <Button
        size="sm"
        onClick={props.onActivate}
        isLoading={props.submitting}
        disabled={!props.token.trim()}
      >
        Activate bot
      </Button>
    </div>
  );
}
